import { createContext, ReactNode, useContext, useEffect, useRef, useState, type AnchorHTMLAttributes } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useLanguage } from '../Contexts/LanguageContext';
import { useServiceLifecycle } from '../Hooks/useServiceLifecycle';
import { lifecycleText, type ServiceLifecycleImage } from '../Services/ServiceLifecycle';
import { Theme } from '../Theme/Theme';
import { useRootStore } from '../Store/useRootStore';
import { Container } from 'reactstrap';
import { MarkdownRenderer } from './Markdown/MarkdownRenderer';
import { Button } from './Button';
import { Link, useInRouterContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { defaultUrlTransform } from 'react-markdown';

type LocalizedNotice = {
  title: string;
  text: string;
  actionLabel: string;
  actionUrl: string | null;
  image?: ServiceLifecycleImage | null;
};
const CardNoticeContext = createContext<LocalizedNotice | null>(null);

const Notice = styled.aside`
  position: sticky;
  top: 0;
  z-index: 1100;
  background: #b3f2e1;
  color: ${Theme.Colors.primaryText};
  border-bottom: 1px solid #008f63;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const Frame = styled.div<{ $noticeHeight: number }>`
  --service-notice-height: ${({ $noticeHeight }) => `${$noticeHeight}px`};
`;

const NoticeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px 24px;
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;

  a {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
  }
`;

const NoticeCopy = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 72ch;
  text-align: center;
`;

const NoticeTitle = styled.strong`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  white-space: pre-line;
`;

const Description = styled.div`
  max-width: 75ch;

  p,
  ul,
  ol,
  blockquote,
  pre {
    margin: 0 0 8px;
  }

  > :last-child {
    margin-bottom: 0;
  }

  p {
    white-space: pre-line;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 1em;
    font-weight: 600;
    margin: 8px 0 4px;
  }

  ul,
  ol {
    padding-left: 1.5em;
  }

  pre {
    max-width: 100%;
    overflow-x: auto;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  a:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }
`;

const CardContainer = styled.div`
  margin-bottom: 24px;
`;

const CardSection = styled.div`
  background: ${Theme.Colors.white};
  padding: 48px 0 24px;

  @media (max-width: 768px) {
    padding-top: 32px;
  }
`;

const CardSectionContent = styled(Container)`
  padding: 0 12px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const Card = styled.aside`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  gap: 16px;
  background: ${Theme.Colors.white};
  border: 1px solid ${Theme.Colors.strokePrimary};
  border-radius: 24px;
  padding: 24px;
  color: ${Theme.Colors.primaryText};
  font-size: 16px;
  line-height: 1.5;
  overflow-wrap: anywhere;

  > div > h2 {
    margin: 0 0 8px;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.5;
    white-space: pre-line;
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const CardCopy = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 72ch;
`;

const CardImage = styled.img`
  display: block;
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  border-radius: 18px;
`;

function NoticeImage({ image }: { image: ServiceLifecycleImage }) {
  const [failed, setFailed] = useState(false);
  return failed ? null : <CardImage src={image.url} alt={image.alt} onError={() => setFailed(true)} />;
}

const CardAction = styled(Button)`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  min-height: 44px;
  && {
    padding: 12px 20px;
    border: 1px solid ${Theme.Colors.primary};
    border-radius: 8px;
    background: ${Theme.Colors.primary};
    color: ${Theme.Colors.primaryText};
    box-shadow: none;
    font: inherit;
    font-weight: 600;
    line-height: 1.5;
    text-decoration: none;
    text-align: center;
    white-space: normal;
  }
  overflow-wrap: anywhere;

  &&:hover,
  &&:active {
    background: #00b882;
    border-color: #00b882;
    color: ${Theme.Colors.primaryText};
  }

  &:focus-visible {
    outline: 2px solid #008f63;
    outline-offset: 4px;
  }
`;

function NoticeLink({ href, children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const inRouter = useInRouterContext();
  if (!href) {
    return <span>{children}</span>;
  }
  let destination: URL;
  try {
    const destinationHref = /^www\./i.test(href) ? `https://${href}` : href;
    destination = new URL(destinationHref, window.location.href);
  } catch {
    return <span>{children}</span>;
  }
  const isWeb = destination.protocol === 'http:' || destination.protocol === 'https:';
  if (!isWeb && destination.protocol !== 'mailto:' && destination.protocol !== 'tel:') {
    return <span>{children}</span>;
  }
  const sameOrigin = destination.origin === window.location.origin;
  if (sameOrigin && inRouter) {
    return (
      <Link {...props} to={`${destination.pathname}${destination.search}${destination.hash}`}>
        {children}
      </Link>
    );
  }
  const external = isWeb && !sameOrigin;
  return (
    <a
      {...props}
      href={destination.href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  );
}

function NoticeMarkdownLink({ href, title, children }: { href?: string; title?: string; children?: ReactNode }) {
  return (
    <NoticeLink href={href} title={title}>
      {children}
    </NoticeLink>
  );
}

function noticeUrlTransform(url: string, key: string) {
  return key === 'href' && /^tel:/i.test(url) ? url : defaultUrlTransform(url);
}

function NoticeAction({ href, label }: { href: string; label: string }) {
  return (
    <CardAction tag={NoticeLink} href={href} data-testid="service-lifecycle-action">
      {label}
    </CardAction>
  );
}

export function ServiceLifecycleCard({ inContent = false }: { inContent?: boolean }) {
  const notice = useContext(CardNoticeContext);
  const { t } = useTranslation('coach-web');
  if (!notice) {
    return null;
  }
  const card = (
    <Card role="status" data-testid="service-lifecycle-card">
      {notice.image && <NoticeImage key={notice.image.url} image={notice.image} />}
      <CardCopy>
        {notice.title && <h2>{notice.title}</h2>}
        <Description>
          <MarkdownRenderer text={notice.text} components={{ a: NoticeMarkdownLink }} urlTransform={noticeUrlTransform} />
        </Description>
      </CardCopy>
      {notice.actionUrl && (
        <NoticeAction
          href={notice.actionUrl}
          label={notice.actionLabel.trim() || t('webinar.learnMore', { defaultValue: 'Learn more' })}
        />
      )}
    </Card>
  );
  return inContent ? (
    <CardContainer>{card}</CardContainer>
  ) : (
    <CardSection data-testid="service-lifecycle-card-section">
      <CardSectionContent>{card}</CardSectionContent>
    </CardSection>
  );
}

export const ServiceLifecycleBoundary = observer(({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('coach-web');
  const { authentication, gymCustomer } = useRootStore();
  const scope = authentication.athleteId
    ? `athlete:${authentication.athleteId}:gym:${gymCustomer.customer?.gymId ?? gymCustomer.currentGym?.id ?? ''}`
    : null;
  const state = useServiceLifecycle(scope);
  const { language } = useLanguage();
  const noticeRef = useRef<HTMLElement>(null);
  const [noticeHeight, setNoticeHeight] = useState(0);
  const text = state ? lifecycleText(state.banner.text, language) : '';
  const title = state ? lifecycleText(state.banner.title ?? {}, language) : '';
  const isCard = state?.banner.presentation === 'CARD';
  const showBanner = Boolean(state?.banner.enabled && text && !isCard);

  useEffect(() => {
    if (!noticeRef.current) {
      setNoticeHeight(0);
      return;
    }
    const observer = new ResizeObserver(([entry]) => setNoticeHeight(entry.target.getBoundingClientRect().height));
    observer.observe(noticeRef.current);
    return () => observer.disconnect();
  }, [showBanner, text, title]);

  const actionLabel = state ? lifecycleText(state.banner.actionLabel, language) : '';

  return (
    <CardNoticeContext.Provider
      value={
        state?.banner.enabled && text && isCard
          ? { title, text, actionLabel, actionUrl: state.banner.actionUrl, image: state.banner.image }
          : null
      }
    >
      <Frame $noticeHeight={showBanner ? noticeHeight : 0}>
        {showBanner && state && (
          <Notice ref={noticeRef} role="status" data-testid="service-lifecycle-banner">
            <NoticeContent>
              <NoticeCopy>
                {title && <NoticeTitle>{title}</NoticeTitle>}
                <Description>
                  <MarkdownRenderer text={text} components={{ a: NoticeMarkdownLink }} urlTransform={noticeUrlTransform} />
                </Description>
              </NoticeCopy>
              {state.banner.actionUrl && (
                <NoticeAction
                  href={state.banner.actionUrl}
                  label={actionLabel.trim() || t('webinar.learnMore', { defaultValue: 'Learn more' })}
                />
              )}
            </NoticeContent>
          </Notice>
        )}
        {children}
      </Frame>
    </CardNoticeContext.Provider>
  );
});
